import { useMemo, useState, type FormEvent } from 'react'
import {
  useHazardLibrary,
  useCreateHazard,
  usePatchHazard,
  useDeleteHazard
} from '../hooks/useHazardLibrary'
import {
  ModalShell, Field, inputClass,
  CancelButton, PrimaryButton, DangerButton, ServerError
} from '../components/modal-form'
import { Icon } from '../components/Icon'
import { useMe } from '../hooks/useMe'
import type { HazardLibraryEntry, HazardLibraryCreate, HazardLibraryPatch } from '../types'

// AdminHazardLibrary — admin-only page for managing the org-level
// hazard catalogue (spec 04 §library). Mirrors the AdminChecklistTemplates
// pattern: list grouped by category, click a row to open the editor,
// new entries via a primary button at the top right. Soft-delete via
// the API; existing EventRisks rows that came from a hazard keep
// their source_library_id intact.
//
// Non-admins land here with a 403 surface — the GET endpoint is open
// to any role (the per-event Risks tab uses it via "Add from library")
// but the write endpoints are admin-gated, so we mirror that on the
// page itself.

export function AdminHazardLibrary() {
  const { data: me } = useMe()
  const { data: hazards, isLoading, error } = useHazardLibrary()
  const [editing, setEditing] = useState<HazardLibraryEntry | 'new' | null>(null)

  // Group by category for visual scanability — most hazards on a
  // heritage railway cluster around the same handful of areas.
  const grouped = useMemo(() => {
    const map = new Map<string, HazardLibraryEntry[]>()
    for (const h of hazards ?? []) {
      const arr = map.get(h.category) ?? []
      arr.push(h)
      map.set(h.category, arr)
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]))
  }, [hazards])

  if (me && !me.isAdmin) {
    return (
      <div className="space-y-5">
        <div>
          <h1 className="text-3xl mb-1">Hazard library</h1>
          <p className="text-ink-soft">Admin only.</p>
        </div>
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
          Editing the hazard library requires admin permissions.
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl mb-1">Hazard library</h1>
          <p className="text-ink-soft">
            The org-level catalogue of common heritage-railway hazards. Event
            managers pull from this list on a per-event Risks tab; entries you
            edit here roll forward to future events without touching existing rows.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="px-4 py-2 text-sm font-semibold rounded bg-green text-parchment hover:bg-green/90 transition-colors"
        >
          + New hazard
        </button>
      </div>

      {isLoading && (
        <div className="text-ink-soft text-sm font-mono">Loading hazards…</div>
      )}

      {error && (
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
          <div className="font-semibold mb-1">Could not load hazards</div>
          <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
        </div>
      )}

      {hazards && hazards.length === 0 && (
        <div className="bg-white border border-line rounded p-10 text-center">
          <div
            className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
            style={{ background: '#f8dbdd', color: '#7a2329' }}
          >
            <Icon name="alert-triangle" size={22} />
          </div>
          <h3 className="font-display text-lg mb-1">No hazards yet</h3>
          <p className="text-sm text-ink-soft mb-4 max-w-md mx-auto">
            The library is empty. Add hazards here and event managers will be
            able to pull them onto their per-event Risks register.
          </p>
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="px-4 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 transition-colors"
          >
            + Add the first hazard
          </button>
        </div>
      )}

      {grouped.map(([category, entries]) => (
        <section key={category} className="bg-white border border-line rounded overflow-hidden">
          <header className="px-4 py-2.5 border-b border-line bg-parchment/60 flex items-baseline justify-between">
            <h2 className="text-sm font-semibold">{category}</h2>
            <span className="text-xs text-ink-soft font-mono">
              {entries.length} hazard{entries.length === 1 ? '' : 's'}
            </span>
          </header>
          <div className="divide-y divide-line">
            {entries.map((h) => (
              <button
                key={h.id}
                type="button"
                onClick={() => setEditing(h)}
                className="w-full text-left px-4 py-3 flex items-start gap-4 hover:bg-parchment/40 transition-colors"
              >
                <Icon name="alert-triangle" size={16} className="text-oxblood flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{h.hazard}</div>
                  {h.suggestedControls && (
                    <div className="text-xs text-ink-soft mt-0.5 line-clamp-1">
                      {h.suggestedControls}
                    </div>
                  )}
                </div>
                <RiskBadge likelihood={h.defaultLikelihood} severity={h.defaultSeverity} />
              </button>
            ))}
          </div>
        </section>
      ))}

      {editing && (
        <HazardEditor
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

// ---- Risk score badge ----------------------------------------------------
//
// The product score (L × S) ∈ [1, 25]. Same colour stops as the per-event
// Risks tab matrix so admins reading the library see consistent visual
// language across surfaces.
function RiskBadge({ likelihood, severity }: { likelihood: number; severity: number }) {
  const score = likelihood * severity
  const style =
    score >= 15 ? { background: '#f8dbdd', color: '#7a2329' }
    : score >= 10 ? { background: '#f5edd5', color: '#9b7f2a' }
    : { background: '#dcebe5', color: '#0a4a3a' }
  return (
    <span
      className="text-[11px] font-mono font-bold px-2 py-0.5 rounded whitespace-nowrap"
      style={style}
      title={`Default likelihood ${likelihood} × severity ${severity}`}
    >
      {likelihood}×{severity}={score}
    </span>
  )
}

// ---- Editor modal --------------------------------------------------------

function HazardEditor({
  initial, onClose
}: {
  initial: HazardLibraryEntry | null
  onClose: () => void
}) {
  const isEditing = initial != null
  const createMut = useCreateHazard()
  const patchMut  = usePatchHazard()
  const deleteMut = useDeleteHazard()

  const [hazard, setHazard]                   = useState(initial?.hazard ?? '')
  const [category, setCategory]               = useState(initial?.category ?? '')
  const [defaultLikelihood, setDefLikelihood] = useState(initial?.defaultLikelihood ?? 3)
  const [defaultSeverity, setDefSeverity]     = useState(initial?.defaultSeverity ?? 3)
  const [suggestedControls, setControls]      = useState(initial?.suggestedControls ?? '')

  const trimmedHazard = hazard.trim()
  const trimmedCategory = category.trim()
  const fieldErrors: { hazard?: string; category?: string } = {}
  if (trimmedHazard.length === 0) fieldErrors.hazard = 'Hazard is required'
  else if (trimmedHazard.length > 200) fieldErrors.hazard = 'Hazard must be 200 characters or fewer'
  if (trimmedCategory.length === 0) fieldErrors.category = 'Category is required'
  else if (trimmedCategory.length > 80) fieldErrors.category = 'Category must be 80 characters or fewer'

  const isValid = Object.keys(fieldErrors).length === 0
  const saving = createMut.isPending || patchMut.isPending
  const deleting = deleteMut.isPending
  const serverError = (createMut.error ?? patchMut.error ?? deleteMut.error) as Error | null

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!isValid || saving) return
    const body: HazardLibraryCreate & HazardLibraryPatch = {
      hazard: trimmedHazard,
      category: trimmedCategory,
      defaultLikelihood,
      defaultSeverity,
      suggestedControls: suggestedControls.trim() || null
    }
    if (isEditing && initial) {
      patchMut.mutate({ id: initial.id, body }, { onSuccess: () => onClose() })
    } else {
      createMut.mutate(body, { onSuccess: () => onClose() })
    }
  }

  function handleDelete() {
    if (!initial) return
    const ok = window.confirm(
      `Remove "${initial.hazard}" from the library? Existing event risks that came from it keep working — but new "Add from library" lookups won't include it.`
    )
    if (!ok) return
    deleteMut.mutate(initial.id, { onSuccess: () => onClose() })
  }

  return (
    <ModalShell
      title={isEditing ? 'Edit hazard' : 'New hazard'}
      onClose={onClose}
      onSubmit={handleSubmit}
      size="lg"
      footer={
        <>
          {isEditing && (
            <DangerButton onClick={handleDelete} disabled={saving || deleting}>
              {deleting ? 'Removing…' : 'Remove'}
            </DangerButton>
          )}
          <CancelButton onClick={onClose} disabled={saving} />
          <PrimaryButton disabled={!isValid || saving}>
            {saving ? 'Saving…' : isEditing ? 'Save changes' : 'Add hazard'}
          </PrimaryButton>
        </>
      }
      body={
        <div className="space-y-4">
          <Field label="Hazard" error={fieldErrors.hazard}>
            <input
              type="text"
              value={hazard}
              onChange={(e) => setHazard(e.target.value)}
              maxLength={200}
              autoFocus
              required
              className={inputClass(!!fieldErrors.hazard)}
              placeholder="e.g. Slipping on wet platform edge"
            />
          </Field>

          <Field label="Category" error={fieldErrors.category} hint="Used to group the list — keep wording consistent.">
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              maxLength={80}
              required
              className={inputClass(!!fieldErrors.category)}
              placeholder="e.g. Platforms, Locomotive, Public"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Default likelihood" hint="1 (rare) – 5 (almost certain)">
              <ScoreSelect value={defaultLikelihood} onChange={setDefLikelihood} />
            </Field>
            <Field label="Default severity" hint="1 (negligible) – 5 (catastrophic)">
              <ScoreSelect value={defaultSeverity} onChange={setDefSeverity} />
            </Field>
          </div>

          <Field
            label="Suggested controls (optional)"
            hint="Free text — copied to per-event risks as the starting mitigation."
          >
            <textarea
              value={suggestedControls}
              onChange={(e) => setControls(e.target.value)}
              rows={4}
              className={inputClass(false)}
              placeholder="Anti-slip strips on platform edge; PPE; visual checks every operating hour."
            />
          </Field>

          {serverError && (
            <ServerError title="Could not save" error={serverError} />
          )}
        </div>
      }
    />
  )
}

function ScoreSelect({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className={inputClass(false)}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <option key={n} value={n}>{n}</option>
      ))}
    </select>
  )
}
